import { useState, useEffect, useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";

import {
  ClassicEditor,
  AccessibilityHelp,
  Alignment,
  Autoformat,
  AutoImage,
  Autosave,
  BalloonToolbar,
  Base64UploadAdapter,
  BlockQuote,
  Bold,
  CloudServices,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  Markdown,
  MediaEmbed,
  PageBreak,
  Paragraph,
  PasteFromMarkdownExperimental,
  PasteFromOffice,
  RemoveFormat,
  SelectAll,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Underline,
  Undo,
} from "ckeditor5";
import { ExportPdf, ExportWord, ImportWord } from "ckeditor5-premium-features";


import 'ckeditor5/ckeditor5.css';
import './ckEditor.css'
import styles from "react-loading-overlay-ts/dist/styles";


const CkEditor = () => {
	const editorContainerRef = useRef(null);
	const editorRef = useRef(null);
	const [isLayoutReady, setIsLayoutReady] = useState(false);

	useEffect(() => {
		setIsLayoutReady(true);

		return () => setIsLayoutReady(false);
	}, []);

	// Custom CKEditor configuration
	const editorConfiguration = 
	{
		toolbar: {
			items: [
				"undo",
				"redo",
				"|",
				"sourceEditing",
				"importWord",
				"exportWord", 
				"exportPdf", 
				"findAndReplace",
				"|",
				"heading",
				"style",
				"|",
				"fontSize",
				"fontFamily",
				"fontColor",
				"fontBackgroundColor",
				"|",
				"bold",
				"italic",
				"underline",
				"strikethrough",
				"subscript",
				"superscript",
				"removeFormat",
				"|",
				"specialCharacters",
				"pageBreak",
				"link",
				"insertImage",
				"insertImageViaUrl",
				"mediaEmbed",
				"insertTable",
				"highlight",
				"blockQuote",
				"|",
				"alignment",
				"|",
				"outdent",
				"indent",
			],
			shouldNotGroupWhenFull: true,
			toolbarSticky:true
		},
		plugins: [
			AccessibilityHelp,
			Alignment,
			Autoformat,
			AutoImage,
			Autosave,
			BalloonToolbar,
			Base64UploadAdapter,
			BlockQuote,
			Bold,
			CloudServices,
			Essentials,
			ExportPdf,
			ExportWord,
			FindAndReplace,
			FontBackgroundColor,
			FontColor,
			FontFamily,
			FontSize,
			GeneralHtmlSupport,
			Heading,
			Highlight,
			ImageBlock,
			ImageCaption,
			ImageInline,
			ImageInsert,
			ImageInsertViaUrl,
			ImageResize,
			ImageStyle,
			ImageTextAlternative,
			ImageToolbar,
			ImageUpload,
			ImportWord,
			Indent,
			IndentBlock,
			Italic,
			Link,
			LinkImage,
			// Markdown,
			MediaEmbed,
			PageBreak,
			Paragraph,
			// PasteFromMarkdownExperimental,
			PasteFromOffice,
			RemoveFormat,
			SelectAll,
			SourceEditing,
			SpecialCharacters,
			SpecialCharactersArrows,
			SpecialCharactersCurrency,
			SpecialCharactersEssentials,
			SpecialCharactersLatin,
			SpecialCharactersMathematical,
			SpecialCharactersText,
			Strikethrough,
			Style,
			Subscript,
			Superscript,
			Table,
			TableCaption,
			TableCellProperties,
			TableColumnResize,
			TableProperties,
			TableToolbar,
			TextTransformation,
			Underline,
			Undo,
		],
		ExportPdf,
		ExportWord, 
		balloonToolbar: ["bold", "italic", "|", "link", "insertImage"],
		fontFamily: {
			supportAllValues: true,
		},
		fontSize: {
			options: [10, 12, 14, "default", 18, 20, 22],
			supportAllValues: true,
		},
		image: {
			toolbar: [
				"toggleImageCaption",
				"imageTextAlternative",
				"|",
				"imageStyle:inline",
				"imageStyle:wrapText",
				"imageStyle:breakText",
				"|",
				"resizeImage",
			],
		},
		exportWord: {
			stylesheets: [
			  /* This path should point to application stylesheets. */
			  /* See: https://ckeditor.com/docs/ckeditor5/latest/features/converters/export-word.html */
			  "./Editor.css",
			  /* Export Word needs access to stylesheets that style the content. */
			  "https://cdn.ckeditor.com/ckeditor5/43.2.0/ckeditor5.css",
			  "https://cdn.ckeditor.com/ckeditor5-premium-features/43.2.0/ckeditor5-premium-features.css",
			],
			fileName: `document.docx`,
			converterOptions: {
			  document: {
				orientation: "portrait",
				size: "A4",
				margins: {
				  top: "20mm",
				  bottom: "20mm",
				  right: "24mm",
				  left: "24mm",
				},
			  },
			},
		  },
		exportPdf: {
			stylesheets: [
			  /* This path should point to application stylesheets. */
			  /* See: https://ckeditor.com/docs/ckeditor5/latest/features/converters/export-pdf.html */
			  "./Editor.css",
			  /* Export PDF needs access to stylesheets that style the content. */
			  "https://cdn.ckeditor.com/ckeditor5/43.2.0/ckeditor5.css",
			  "https://cdn.ckeditor.com/ckeditor5-premium-features/43.2.0/ckeditor5-premium-features.css",
			],
			fileName: `document.pdf`,
			converterOptions: {
			  format: "A4",
			  margin_top: "20mm",
			  margin_bottom: "20mm",
			  margin_right: "24mm",
			  margin_left: "24mm",
			  page_orientation: "portrait",
			},
		  },
		table: {
			contentToolbar: [
				"tableColumn",
				"tableRow",
				"mergeTableCells",
				"tableProperties",
				"tableCellProperties",
			],
		},
		menuBar: {
			isVisible: true,
		},
		style: {
			definitions: [
				{
					name: "Article category",
					element: "h3",
					classes: ["category"],
				},
				{
					name: "Title",
					element: "h2",
					classes: ["document-title"],
				},
				{
					name: "Subtitle",
					element: "h3",
					classes: ["document-subtitle"],
				},
				{
					name: "Info box",
					element: "p",
					classes: ["info-box"],
				},
				{
					name: "Side quote",
					element: "blockquote",
					classes: ["side-quote"],
				},
				{
					name: "Marker",
					element: "span",
					classes: ["marker"],
				},
				{
					name: "Spoiler",
					element: "span",
					classes: ["spoiler"],
				},
				{
					name: "Code (dark)",
					element: "pre",
					classes: ["fancy-code fancy-code-dark"],
				},
				{
					name: "Code (bright)",
					element: "pre",
					classes: ["fancy-code fancy-code-bright"],
				},
			],
		},
		htmlSupport: {
			allow:[
				{
					name: `/^.*$/`, // Allows all HTML tags
					styles: `true`, // Allows all inline styles
					attributes: `true`, // Allows all attributes
					classes: `true`,
				}
			]
		}, 
		
		shouldNotGroupWhenFull: true,
	};

	  const setHTML = (myRef : any, data : any, staff : any) => {
		const box = myRef.current.editor;
	
		box.setData(data);
		if (staff === false) {
		  box.enableReadOnlyMode("myUniqueLock");
		}
	  };
	
	  const getHTML = (myRef : any) => {
		const box = myRef.current?.editor || null;
		return box?.getData() || null;
	  };
	  const editorConfig = 
	  {
		  plugins: [
			AccessibilityHelp,
			Alignment,
			Autoformat,
			AutoImage,
			
			Autosave,
			BalloonToolbar,
			Base64UploadAdapter,
			BlockQuote,
			Bold,
			Essentials,
			FindAndReplace,
			FontBackgroundColor,
			FontColor,
			FontFamily,
			FontSize,
			GeneralHtmlSupport,
			
			Heading,
			ImageBlock,
			ImageCaption,
			ImageInline,
			ImageInsert,
			ImageInsertViaUrl,
			ImageResize,
			ImageStyle,
			ImageTextAlternative,
			ImageToolbar,
			ImageUpload,
			Indent,
			IndentBlock,
			Italic,
			Link,
			LinkImage,
			MediaEmbed,
			PageBreak,
			Paragraph,
			PasteFromOffice,
			RemoveFormat,
			SelectAll,
			SourceEditing,
			SpecialCharacters,
			SpecialCharactersArrows,
			SpecialCharactersCurrency,
			SpecialCharactersEssentials,
			SpecialCharactersLatin,
			SpecialCharactersMathematical,
			SpecialCharactersText,
			Strikethrough,
			Style,
			Subscript,
			Superscript,
			Table,
			TableCaption,
			TableCellProperties,
			TableColumnResize,
			TableProperties,
			TableToolbar,
			TextTransformation,
			Underline,
			Undo,
		  ],
		fontFamily: {
			supportAllValues: true,
		  },
		  fontSize: {
			options: [10, 12, 14, "default", 18, 20, 22],
			supportAllValues: true,
		  },
		 
		  image: {
			toolbar: [
			  "toggleImageCaption",
			  "imageTextAlternative",
			  "|",
			  "imageStyle:inline",
			  "imageStyle:wrapText",
			  "imageStyle:breakText",
			  "|",
			  "resizeImage",
			],
		  },
		  table: {
			contentToolbar: [
			  "tableColumn",
			  "tableRow",
			  "mergeTableCells",
			  "tableProperties",
			  "tableCellProperties",
			],
		  },
		  menuBar: {
			isVisible: false,
		  },
		  style: {
			definitions: [
			  {
				name: "Article category",
				element: "h3",
				classes: ["category"],
			  },
			  {
				name: "Title",
				element: "h2",
				classes: ["document-title"],
			  },
			  {
				name: "Subtitle",
				element: "h3",
				classes: ["document-subtitle"],
			  },
			  {
				name: "Info box",
				element: "p",
				classes: ["info-box"],
			  },
			  {
				name: "Side quote",
				element: "blockquote",
				classes: ["side-quote"],
			  },
			  {
				name: "Marker",
				element: "span",
				classes: ["marker"],
			  },
			  {
				name: "Spoiler",
				element: "span",
				classes: ["spoiler"],
			  },
			  {
				name: "Code (dark)",
				element: "pre",
				classes: ["fancy-code", "fancy-code-dark"],
			  },
			  {
				name: "Code (bright)",
				element: "pre",
				classes: ["fancy-code", "fancy-code-bright"],
			  },
			],
		  },
		  extraAllowedContent: "table{*};td{*};th{*}",
		  isReadOnly: true,
	  };

	  const CKEditors = (props : any) => {
		const { placeholder = '',myRef, htmlBox, toolstate, onChange,onBlur } = props;
		const box = htmlBox || "";
		return (
		  <div>
			{/* <button onClick={htmlSet}>Set Data with HTML</button> */}
			<div className="main-container">
			  <div
				className="editor-container editor-container_classic-editor editor-container_include-style"
				ref={editorContainerRef}
			  >
				<div>
				  <div ref={myRef}>
					{isLayoutReady && (
					  <CKEditor
						editor={ClassicEditor}
						onReady={(editor) => {
						  myRef.current = { editor };
						  setHTML(myRef, box, toolstate);
						}}
						
						config={{...editorConfiguration,placeholder:placeholder}}
						onChange={(event, editor) => {
							const data = editor.getData(); // Get the current editor content
							
							if (onChange) {
							  onChange(data); // Pass the data to the parent or other function via props
							}
						  }}
						onBlur={(event, editor) => {
							const data = editor.getData();
							if (onBlur) {
								onBlur(data); // You might also want to save data on blur
							}
						}}
					  />
					)}
				  </div>
				</div>
			  </div>
			</div>
		  </div>
		);
	  };
	
	  const ROCKEditors = (props: any) => {
		const { myRef, htmlBox, toolstate, onChange, onBlur, width } = props;
		const box = htmlBox || "";
		return (
		  <div>
			<div className="main-container">
			  <div
				style={{ width: width, border: '1px solid #ccc' }}
				className="editor-container editor-container_classic-editor editor-container_include-style"
				ref={editorContainerRef}
			  >
				<div>
				  <div ref={myRef}>
					{isLayoutReady && (
					  <CKEditor
						editor={ClassicEditor}
						onReady={(editor) => {
						  myRef.current = { editor };
						  setHTML(myRef, box, toolstate);
						}}
						config={editorConfig}
						onChange={(event, editor) => {
						  const data = editor.getData();
						  if (onChange) {
							onChange(data);
						  }
						}}
						onBlur={(event, editor) => {
						  const data = editor.getData();
						  if (onBlur) {
							onBlur(data);
						  }
						}}
					  />
					)}
				  </div>
				</div>
			  </div>
			</div>
		  </div>
		);
	  };
	  
	
	

	return (
		{
			
			CKEditors,
			getHTML,
			ROCKEditors
		  }
		
	);
}


export default CkEditor;
